// src/components/RichTextEditor.js
import React, { useRef, useEffect } from "react";
import { Box, IconButton, Divider, ButtonGroup, Tooltip } from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatColorText,
  FormatColorFill,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatSize,
} from "@mui/icons-material";

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const colorPickerRef = useRef(null);
  const bgColorPickerRef = useRef(null);

  // Initialize editor with HTML content
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  // Handle content change
  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  // Execute formatting command
  const executeCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    // Trigger change event after formatting
    handleInput();
  };

  // Format buttons configuration
  const formatButtons = [
    {
      command: "bold",
      icon: <FormatBold />,
      tooltip: "Bold (Ctrl+B)",
    },
    {
      command: "italic",
      icon: <FormatItalic />,
      tooltip: "Italic (Ctrl+I)",
    },
    {
      command: "underline",
      icon: <FormatUnderlined />,
      tooltip: "Underline (Ctrl+U)",
    },
  ];

  const fontSizes = [
    { label: "Small", value: "1" },
    { label: "Normal", value: "3" },
    { label: "Large", value: "5" },
    { label: "Huge", value: "7" },
  ];

  return (
    <Box>
      {/* Formatting Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1,
          backgroundColor: "action.hover",
          borderRadius: "4px 4px 0 0",
          borderBottom: "1px solid",
          borderColor: "divider",
          flexWrap: "wrap",
        }}
      >
        {/* Text Style */}
        <ButtonGroup size="small" variant="outlined">
          {formatButtons.map((btn) => (
            <Tooltip key={btn.command} title={btn.tooltip}>
              <IconButton
                onClick={() => executeCommand(btn.command)}
                size="small"
              >
                {btn.icon}
              </IconButton>
            </Tooltip>
          ))}
        </ButtonGroup>

        <Divider orientation="vertical" flexItem />

        {/* Font Size */}
        <ButtonGroup size="small" variant="outlined">
          {fontSizes.map((size) => (
            <Tooltip key={size.value} title={`Font Size: ${size.label}`}>
              <IconButton
                onClick={() => executeCommand("fontSize", size.value)}
                size="small"
                sx={{ minWidth: 40 }}
              >
                <Box sx={{ fontSize: size.value === "1" ? "10px" : size.value === "3" ? "14px" : size.value === "5" ? "18px" : "24px" }}>
                  A
                </Box>
              </IconButton>
            </Tooltip>
          ))}
        </ButtonGroup>

        <Divider orientation="vertical" flexItem />

        {/* Text Color */}
        <Tooltip title="Text Color">
          <IconButton
            size="small"
            onClick={() => colorPickerRef.current?.click()}
          >
            <FormatColorText />
          </IconButton>
        </Tooltip>
        <input
          ref={colorPickerRef}
          type="color"
          style={{ display: "none" }}
          onChange={(e) => executeCommand("foreColor", e.target.value)}
        />

        {/* Background Color */}
        <Tooltip title="Highlight Color">
          <IconButton
            size="small"
            onClick={() => bgColorPickerRef.current?.click()}
          >
            <FormatColorFill />
          </IconButton>
        </Tooltip>
        <input
          ref={bgColorPickerRef}
          type="color"
          style={{ display: "none" }}
          onChange={(e) => executeCommand("backColor", e.target.value)}
        />

        <Divider orientation="vertical" flexItem />

        {/* Text Alignment */}
        <ButtonGroup size="small" variant="outlined">
          <Tooltip title="Align Left">
            <IconButton
              onClick={() => executeCommand("justifyLeft")}
              size="small"
            >
              <FormatAlignLeft />
            </IconButton>
          </Tooltip>
          <Tooltip title="Align Center">
            <IconButton
              onClick={() => executeCommand("justifyCenter")}
              size="small"
            >
              <FormatAlignCenter />
            </IconButton>
          </Tooltip>
          <Tooltip title="Align Right">
            <IconButton
              onClick={() => executeCommand("justifyRight")}
              size="small"
            >
              <FormatAlignRight />
            </IconButton>
          </Tooltip>
        </ButtonGroup>
      </Box>

      {/* Editable Area */}
      <Box
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        suppressContentEditableWarning
        sx={{
          minHeight: 500,
          maxHeight: 600,
          overflowY: "auto",
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "0 0 4px 4px",
          backgroundColor: "background.paper",
          outline: "none",
          "&:focus": {
            borderColor: "primary.main",
          },
          "&:empty:before": {
            content: `"${placeholder || 'Start typing...'}"`,
            color: "text.secondary",
            fontStyle: "italic",
          },
        }}
      />
    </Box>
  );
};

export default RichTextEditor;
